/**
 * UserAvatar — el avatar de una persona del equipo, en un solo lugar.
 * Muestra la foto que subió en su perfil (EQUIPO.foto_url) y, si no tiene o no carga, sus iniciales.
 * Se identifica por `id`, `email` o `nombre` (o se le pasa `foto` directo); las fotos salen de una sola
 * lectura de /api/equipo compartida por toda la app. Al subir una foto nueva, `invalidarFotos()` la renueva en todas partes.
 *   <UserAvatar id={c.autor_id} nombre={c.autor?.nombre} className="h-6 w-6" />
 */
import { useEffect, useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar.jsx";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";

let indice = null;
let cargando = null;
const oyentes = new Set();

const norm = (v) => String(v || "").trim().toLowerCase();

function construir(lista) {
  const porId = new Map(), porEmail = new Map(), porNombre = new Map();
  for (const m of lista) {
    if (!m?.foto_url) continue;
    if (m.id) porId.set(m.id, m.foto_url);
    if (m.email) porEmail.set(norm(m.email), m.foto_url);
    if (m.nombre) porNombre.set(norm(m.nombre), m.foto_url);
  }
  return { porId, porEmail, porNombre };
}

function cargar() {
  if (indice) return Promise.resolve();
  if (cargando) return cargando;
  cargando = api.getEquipo()
    .then(r => { indice = construir(r?.equipo ?? (Array.isArray(r) ? r : [])); })
    .catch(() => { indice = construir([]); })
    .finally(() => { cargando = null; oyentes.forEach(f => f()); });
  return cargando;
}

/** Fuerza releer las fotos (se llama justo después de subir o borrar una). */
export function invalidarFotos() {
  indice = null;
  cargando = null;
  oyentes.forEach(f => f());
}

export function fotoDe({ id, email, nombre } = {}) {
  if (!indice) return null;
  return (id && indice.porId.get(id)) || (email && indice.porEmail.get(norm(email))) || (nombre && indice.porNombre.get(norm(nombre))) || null;
}

function useFotoDe(ref) {
  const [, forzar] = useState(0);
  useEffect(() => {
    const f = () => forzar(n => n + 1);
    oyentes.add(f);
    cargar();
    return () => { oyentes.delete(f); };
  }, []);
  return fotoDe(ref);
}

export function inicialesDe(nombre) {
  return String(nombre || "?").split(" ").filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join("") || "?";
}

export default function UserAvatar({ user, id, email, nombre, foto, className, fallbackClassName }) {
  const ref = { id: id ?? user?.id, email: email ?? user?.email, nombre: nombre ?? user?.nombre };
  const resuelta = useFotoDe(ref);
  const src = foto || user?.foto_url || resuelta || null;
  return (
    <Avatar className={cn("h-7 w-7", className)}>
      {src && <AvatarImage src={src} alt={ref.nombre || ""} />}
      <AvatarFallback className={cn("bg-[#CFFAFE] text-[#155E75] text-[12px] font-extrabold", fallbackClassName)}>
        {inicialesDe(ref.nombre)}
      </AvatarFallback>
    </Avatar>
  );
}
